import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Check, ShieldCheck, RefreshCw, Sparkles, Shield, Landmark, Siren } from "lucide-react";
import PlayStoreButton from "@/components/PlayStoreButton";

const PLAN_ICONS = [Shield, Landmark, Siren];

const PlanCard = ({ plan, testId, Icon }) => {
  const highlighted = !!plan.highlighted;
  return (
    <div
      data-testid={testId}
      className={`relative rounded-3xl p-7 sm:p-8 border transition-all ${
        highlighted
          ? "bg-slate-950 text-white border-amber-500/40 lg:scale-[1.03] shadow-2xl shadow-blue-950/25"
          : "bg-white border-blue-900/10 text-slate-900 card-lift"
      }`}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 bg-amber-500 text-slate-900 text-[11px] font-extrabold tracking-wider uppercase px-3.5 py-1.5 rounded-full shadow-lg">
            {plan.badge}
          </span>
        </div>
      )}

      <div className="flex items-start gap-4">
        <div
          className={`h-12 w-12 rounded-xl flex items-center justify-center ${
            highlighted
              ? "bg-amber-500/20 text-amber-400"
              : "bg-blue-100 text-blue-900"
          }`}
        >
          <Icon size={22} strokeWidth={2.2} />
        </div>
        <div>
          <h3 className="text-2xl font-extrabold tracking-tight">{plan.name}</h3>
          <p
            className={`text-sm ${
              highlighted ? "text-slate-300" : "text-slate-500"
            }`}
          >
            {plan.sub}
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-end gap-3">
        <span
          className={`text-5xl font-black tracking-tight ${
            highlighted ? "text-amber-400" : "text-blue-950"
          }`}
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {plan.price}
        </span>
        <span
          className={`pb-2 text-sm ${
            highlighted ? "text-slate-300" : "text-slate-500"
          }`}
        >
          /month
        </span>
      </div>

      <ul className="mt-6 space-y-3">
        {plan.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm">
            <span
              className={`h-5 w-5 rounded-full inline-flex items-center justify-center mt-0.5 shrink-0 ${
                highlighted
                  ? "bg-amber-500 text-slate-900"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              <Check size={12} strokeWidth={3} />
            </span>
            <span
              className={`leading-relaxed ${
                highlighted ? "text-slate-200" : "text-slate-700"
              }`}
            >
              {b}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-7">
        <PlayStoreButton
          size="md"
          variant={highlighted ? "light" : "dark"}
          testId={`${testId}-cta`}
          className="w-full justify-center"
        />
      </div>
    </div>
  );
};

const Pricing = () => {
  const { t } = useLanguage();
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-600">
            {t.pricing.eyebrow}
          </span>
          <h2
            data-testid="pricing-title"
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-blue-950 leading-[1.05]"
          >
            {t.pricing.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            {t.pricing.subtitle}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {t.pricing.plans.map((plan, i) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              Icon={PLAN_ICONS[i]}
              testId={`plan-${i}`}
            />
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-600">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            {t.pricing.assurances[0]}
          </span>
          <span className="inline-flex items-center gap-2">
            <RefreshCw size={16} className="text-blue-700" />
            {t.pricing.assurances[1]}
          </span>
          <span className="inline-flex items-center gap-2">
            <Sparkles size={16} className="text-amber-600" />
            {t.pricing.assurances[2]}
          </span>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
